import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Paper,
  Typography,
  Box,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  CircularProgress,
  Skeleton,
  Tooltip,
  Button,
} from "@mui/material";
import { Droppable } from "react-beautiful-dnd";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import Task from "./Task";
import { hexToRgba } from "@/Components/helpers/util";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";

// Redux imports
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectBoardState,
  selectFilters,
  selectGroupingConfig,
  selectLoadingStates,
  selectApiData,
} from "@/features/bughurd/boardSelector";
import {
  setMinimizedColumn,
  updateTask,
  setSelectedItem,
  setCreateTask,
  setSelectedColumnStatus,
} from "@/features/bughurd/slice";
import { useNavigate, useParams } from "react-router-dom";

interface Column {
  id: string;
  title: string;
  color?: string;
  icon?: string;
  tasks: any[];
  totalCount?: number;
  description?: string;
}

interface ColumnProps {
  column: Column;
  tasks: any[];
  onTitleChange: (newTitle: string) => void;
  onDelete: () => void;
  dragHandleProps?: any;
  isInitialLoading?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: (columnId: string) => void;
  hasMore?: boolean;
  refreshData?: () => void;
  // Note: minimizeColumn and isMinimized are now handled by Redux
  onTaskUpdate?: (
    taskId: string,
    updatedTask: any | null,
    newColumnId?: string,
  ) => void;
  canEditColumn?: boolean;
  hideHeader?: boolean;
  noSprintTask?: boolean;
  onCreateTask?: (columnId: string) => void; // Add this prop for create task callback
  reactFlow?: any;
}

const Column: React.FC<ColumnProps> = ({
  column,
  tasks,
  onTitleChange,
  onDelete,
  dragHandleProps,
  isInitialLoading = false,
  isLoadingMore = false,
  onLoadMore,
  hasMore = false,
  refreshData,
  onTaskUpdate,
  canEditColumn = true,
  hideHeader = false,
  noSprintTask = false,
  reactFlow,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Redux selectors
  const boardState = useAppSelector(selectBoardState);
  // const filters = useAppSelector(selectFilters);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const loadingStates = useAppSelector(selectLoadingStates);
  const apiData = useAppSelector(selectApiData);

  // Destructure Redux state
  const { minimizedColumns } = boardState;
  const { groupBy } = groupingConfig;
  const { statusList } = apiData;
  const { isBugsLoading } = loadingStates;

  // Get minimized state from Redux
  const isMinimized = minimizedColumns[column.id] || false;

  // Local state
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal,
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(column?.title);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef<boolean>(false);
  const prevTasksLengthRef = useRef<number>(0);

  const swimlaneColor = column?.color;
  const statusIcon = column?.icon;
  const columnDescription = column?.description || "";

  // Get available status from Redux
  const availableStatus = statusList || [];

  useEffect(() => {
    setTitle(column?.title);
  }, [column?.title]);

  useEffect(() => {
    loadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  useEffect(() => {
    if (tasks?.length > 0) {
      prevTasksLengthRef.current = tasks.length;
    }
  }, [tasks]);

  const handleScroll = useCallback(
    (event: Event) => {
      if (
        !containerRef.current ||
        isInitialLoading ||
        loadingMoreRef.current ||
        !hasMore ||
        !onLoadMore
      ) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const scrollPosition = scrollTop + clientHeight;
      const threshold = scrollHeight - 50;

      if (scrollPosition >= threshold) {
        loadingMoreRef.current = true;
        onLoadMore(column.id);
      }
    },
    [column.id, column.title, isInitialLoading, hasMore, onLoadMore],
  );

  useEffect(() => {
    const currentRef = containerRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const sortedTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];

    // Sort tasks by boardIndex, then by creation date as fallback
    return [...tasks].sort((a, b) => {
      // Primary sort by boardIndex
      const aIndex = a.boardIndex ?? Number.MAX_SAFE_INTEGER;
      const bIndex = b.boardIndex ?? Number.MAX_SAFE_INTEGER;

      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }

      // Fallback sort by creation date (newest first if no boardIndex)
      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [tasks]);

  const handleRenameClick = () => {
    if (!canEditColumn) {
      handleMenuClose();
      return;
    }
    handleMenuClose();
    setTimeout(() => {
      setIsEditing(true);
    }, 100);
  };

  const handleDeleteClick = () => {
    if (!canEditColumn) {
      handleMenuClose();
      return;
    }
    onDelete();
    handleMenuClose();
  };

  const handleTitleSubmit = () => {
    if (!canEditColumn) {
      setTitle(column?.title);
      setIsEditing(false);
      return;
    }

    if (title.trim() && title !== column?.title) {
      onTitleChange(title);
      setIsEditing(false);
    } else {
      setTitle(column?.title);
      setIsEditing(false);
    }
  };

  // Redux-connected minimize handlers
  const handleToggleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newMinimizedState = !isMinimized;
    dispatch(
      setMinimizedColumn({
        columnId: column.id,
        isMinimized: newMinimizedState,
      }),
    );
  };

  const handleMinimizedColumnClick = () => {
    dispatch(
      setMinimizedColumn({
        columnId: column.id,
        isMinimized: false,
      }),
    );
  };

  // Redux-connected task update handler
  const handleTaskUpdate = useCallback(
    (taskId: string, updatedTask: any | null, newColumnId?: string) => {
      // Update Redux state
      dispatch(
        updateTask({
          taskId,
          updatedTask,
          newColumnId,
        }),
      );

      // Call parent callback if provided
      if (onTaskUpdate) {
        onTaskUpdate(taskId, updatedTask, newColumnId);
      }
    },
    [onTaskUpdate],
  );

  // Handle create task
  const handleCreateTask = () => {
    dispatch(setCreateTask(true));
    dispatch(setSelectedColumnStatus(column?.id));
  };

  const getColumnHeaderColor = (): string => {
    if (swimlaneColor) {
      return swimlaneColor;
    }
    return "#4285F2";
  };
  const columnBodyBg =
    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "#f2f2f8";

  const columnStyles = {
    width: isMinimized
      ? 40
      : hideHeader
        ? "100%"
        : "clamp(280px, calc(90vw / 5), 400px)", // Use full width in sub-grouped view
    minWidth: isMinimized ? 40 : hideHeader ? "100%" : 280, // Maintain minimum width
    maxWidth: isMinimized ? 40 : hideHeader ? "100%" : 400, // Set maximum width constraint
    display: "flex",
    flexDirection: "column" as const,
    height: "fit-content",
    transition: "all 0.3s ease",
    flexShrink: 0,
    flexGrow: isMinimized ? 0 : hideHeader ? 0 : 1, // Don't grow in sub-grouped view
    margin: 0,
    padding: 0,
  };

  const renderSkeletonCards = () => {
    return [...Array(4)].map((_, index) => (
      <Skeleton
        key={`skeleton-${index}`}
        variant="rectangular"
        height={85}
        sx={{
          mb: 1,
          borderRadius: 1,
          backgroundColor: "rgba(0, 0, 0, 0.06)",
          animation: "pulse 1.5s ease-in-out infinite",
          "@keyframes pulse": {
            "0%": {
              opacity: 1,
            },
            "50%": {
              opacity: 0.4,
            },
            "100%": {
              opacity: 1,
            },
          },
        }}
      />
    ));
  };

  const getDisplayCount = (): string => {
    const fetchedCount = tasks?.length || 0;
    const total = column?.totalCount;

    if (isInitialLoading) {
      return "...";
    }

    if (total !== undefined && total !== null) {
      return `${fetchedCount}/${total}`;
    }

    return fetchedCount.toString();
  };

  interface StatusIconProps {
    iconName?: string;
    size?: number;
  }
  if (column.title === "TEST LANE") {
  }
  const StatusIcon: React.FC<StatusIconProps> = ({ iconName, size = 24 }) => {
    if (!iconName) return null;

    return (
      <Icon
        icon={iconName}
        width={size}
        height={size}
        style={{
          color:
            theme.palette.mode === "dark"
              ? theme.palette.primary.main
              : "text.secondary",
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
        }}
        onError={() => {
          console.warn(t("Failed to load status icon: 1", { iconName }));
        }}
      />
    );
  };

  const getGroupingDescription = () => {
    switch (groupBy) {
      case "Assignee":
        return t("Grouped by assignee");
      case "Priority":
        return t("Grouped by priority");
      case "Tags":
        return t("Grouped by tags");
      case "Due date":
        return t("Grouped by due date");
      case "Task type":
        return t("Grouped by task type");
      default:
        return t("Grouped by status");
    }
  };

  return (
    <Box sx={columnStyles} {...(canEditColumn ? dragHandleProps : {})}>
      {/* Paper Component with Skeleton Loader */}
      {isInitialLoading ? (
        <Skeleton
          variant="rectangular"
          height={isMinimized ? 160 : 56}
          sx={{
            mb: isMinimized ? 0 : 1,
            borderRadius: 2,
            backgroundColor: "rgba(0, 0, 0, 0.06)",
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%": {
                opacity: 1,
              },
              "50%": {
                opacity: 0.4,
              },
              "100%": {
                opacity: 1,
              },
            },
          }}
        />
      ) : (
        <Paper
          elevation={0}
          onClick={isMinimized ? handleMinimizedColumnClick : undefined}
          sx={{
            backgroundColor: getColumnHeaderColor(),
            p: isMinimized ? 0.5 : 1,
            mb: isMinimized ? 0 : 1,
            borderRadius: 2,
            display: hideHeader ? "none" : "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "text.secondary",
            cursor: isMinimized ? "pointer" : "default",
            transition: "all 0.3s ease",
            height: "auto",
            width: "100%",
            boxSizing: "border-box",
            position: "relative",
            boxShadow: 1,
          }}
        >
          {/* Drag indicator for reorderable columns */}
          {canEditColumn &&
            !isMinimized &&
            dragHandleProps &&
            groupBy === "Status" && (
              <Box
                {...dragHandleProps}
                {...(dragHandleProps.dragListeners || {})}
                data-drag-handle="column"
                sx={{
                  position: "absolute",
                  left: 4,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "grab",
                  display: "flex",
                  alignItems: "center",
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : "text.secondary",
                  zIndex: 10, // Ensure it's above other elements
                  padding: "4px", // Increase touch/click area
                  borderRadius: "4px",
                  "&:hover": {
                    color: "text.primary",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                  "&:active": {
                    cursor: "grabbing",
                  },
                }}
                onMouseDown={(e) => {
                  // Prevent event from bubbling to prevent task drag interference
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  // Prevent event from bubbling on touch devices
                  e.stopPropagation();
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: 16 }} />
              </Box>
            )}

          {isMinimized ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                py: 1,
                px: 0.5,
                height: "auto",
              }}
            >
              {statusIcon ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    color: "text.secondary",
                  }}
                >
                  <StatusIcon iconName={statusIcon} size={24} />
                </Box>
              ) : isInitialLoading ? (
                <Skeleton variant="rectangular" width={24} height={24} />
              ) : null}

              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "50%",
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "8px",
                    fontWeight: 500,
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : "text.secondary",
                  }}
                >
                  {getDisplayCount()}
                </Typography>
              </Box>

              <Tooltip
                title={`${columnDescription} - ${getGroupingDescription()}`}
                placement="right"
              >
                <Typography
                  sx={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    fontWeight: 500,
                    fontSize: "11px",
                    textAlign: "center",
                    my: 1,
                    maxHeight: 120,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : "text.secondary",
                  }}
                >
                  {column?.title}
                </Typography>
              </Tooltip>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleMinimize(e);
                }}
                sx={{
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : "text.secondary",
                  mt: 1,
                  padding: 0.2,
                  "& svg": {
                    fontSize: "0.85rem",
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  ml:
                    canEditColumn && dragHandleProps && groupBy === "Status"
                      ? 3
                      : 0,
                  // Ensure this doesn't interfere with column dragging
                  pointerEvents: "auto",
                }}
              >
                {(isInitialLoading || isBugsLoading || statusIcon) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 1,
                      width: 24,
                      height: 24,
                    }}
                  >
                    {isInitialLoading || isBugsLoading ? (
                      <Skeleton
                        variant="circular"
                        width={24}
                        height={24}
                        color="red"
                      />
                    ) : (
                      <StatusIcon iconName={statusIcon} size={24} />
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "8px",
                      fontWeight: 500,
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.primary.main
                          : "text.secondary",
                    }}
                  >
                    {isInitialLoading ? "..." : getDisplayCount()}
                  </Typography>
                </Box>

                {isEditing &&
                canEditColumn &&
                DEFEKT_TRACKER.UPDATE(myPermissions) ? (
                  <TextField
                    size="small"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    autoFocus
                    onBlur={handleTitleSubmit}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTitleSubmit();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        // backgroundColor: "white",
                        color:
                          theme.palette.mode === "dark"
                            ? theme.palette.primary.main
                            : "text.secondary",
                      },
                      flexGrow: 1,
                    }}
                  />
                ) : (
                  <Tooltip
                    title={`${columnDescription} - ${getGroupingDescription()}`}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        cursor: canEditColumn ? "pointer" : "default",
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: statusIcon ? 110 : 150,
                        color:
                          theme.palette.mode === "dark"
                            ? theme.palette.primary.main
                            : "text.secondary",
                      }}
                      onClick={
                        canEditColumn ? () => setIsEditing(true) : undefined
                      }
                    >
                      {column?.title}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {!isLoadingMore && !isInitialLoading && tasks.length > 0 && (
                  <Tooltip title={t("create task")}>
                    <IconButton
                      size="small"
                      onClick={handleCreateTask}
                      aria-label={t("create task")}
                      sx={{
                        color: "primary.main",
                        backgroundColor: "transparent",
                        mr: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  onClick={handleToggleMinimize}
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : "text.secondary",
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  aria-controls={open ? "column-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : "text.secondary",
                  }}
                >
                  <MoreHorizIcon />
                </IconButton>
              </Box>
            </>
          )}

          <Menu
            id="column-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "column-menu-button",
            }}
          >
            {!DEFEKT_TRACKER.HIDE_RENAME(myPermissions) && (
              <MenuItem
                onClick={handleRenameClick}
                disabled={
                  !canEditColumn || !DEFEKT_TRACKER.RENAME(myPermissions)
                }
                sx={{
                  opacity: canEditColumn ? 1 : 0.5,
                }}
              >
                <EditIcon sx={{ mr: 1, fontSize: 16 }} />
                {t("Rename")}
                {!canEditColumn && (
                  <Typography
                    variant="caption"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    {t("(Status only)")}
                  </Typography>
                )}
              </MenuItem>
            )}
            {!DEFEKT_TRACKER.HIDE_DELETE(myPermissions) && (
              <MenuItem
                onClick={handleDeleteClick}
                disabled={
                  !canEditColumn || !DEFEKT_TRACKER.DELETE(myPermissions)
                }
                sx={{
                  opacity: canEditColumn ? 1 : 0.5,
                }}
              >
                <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
                {t("Delete")}
                {!canEditColumn && (
                  <Typography
                    variant="caption"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    {t("(Status only)")}
                  </Typography>
                )}
              </MenuItem>
            )}
          </Menu>
        </Paper>
      )}

      {/* Show grouping info for non-status groupings */}
      {!isMinimized && !isInitialLoading && groupBy !== "Status" && (
        <Box
          sx={{
            px: 1,
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "10px",
              fontStyle: "italic",
            }}
          >
            {getGroupingDescription()}
          </Typography>
        </Box>
      )}

      {!isMinimized && (
        <Droppable
          droppableId={column?.id}
          ignoreContainerClipping={true}
          isCombineEnabled={true}
        >
          {(provided, snapshot) => (
            <Box
              ref={(el) => {
                provided.innerRef(el);
                // containerRef.current = el;
              }}
              {...provided.droppableProps}
              // Prevent column drag when interacting with task area
              onMouseDown={(e) => {
                // Only prevent if the target is not the drag handle
                const isDragHandle = (e.target as HTMLElement).closest(
                  '[data-drag-handle="column"]',
                );
                if (!isDragHandle) {
                  e.stopPropagation();
                }
              }}
            >
              {isInitialLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  {renderSkeletonCards()}
                </Box>
              ) : (
                <Box
                  ref={containerRef}
                  sx={{
                    minHeight: 100,
                    maxHeight: "calc(100vh - 255px)",
                    overflowY: "auto",
                    backgroundColor: snapshot.isDraggingOver
                      ? hexToRgba("#4285F4", 0.08)
                      : columnBodyBg,
                    border: (theme) =>
                      snapshot.isDraggingOver &&
                      `1px dashed ${theme.palette.primary.main}`,
                    transition: "background-color 0.2s ease",
                    borderRadius: 2,
                    p: 1.5,
                    pb: 2,
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    position: "relative",
                    // IMPORTANT: Ensure task container doesn't interfere with column drag
                    pointerEvents: "auto",
                    boxShadow: 1,
                  }}
                >
                  {/* {sortedTasks?.map((task, index) => ( */}
                  {tasks?.map((task, index) => (
                    <Task
                      key={task?.id || task?._id}
                      task={task}
                      index={index}
                      refreshColumn={refreshData}
                      onTaskUpdate={handleTaskUpdate}
                      availableStatus={availableStatus}
                      reactFlow={reactFlow}
                    />
                  ))}

                  {provided.placeholder}

                  {isLoadingMore && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 2,
                        mt: 1,
                        mb: "88px",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        borderRadius: 1,
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        boxShadow: 1,
                      }}
                    >
                      <CircularProgress
                        size={20}
                        thickness={4}
                        sx={{
                          color: "primary.main",
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      >
                        {t("Loading more tasks...")}
                      </Typography>
                    </Box>
                  )}

                  {!isLoadingMore &&
                    !isInitialLoading &&
                    // !hasMore &&
                    tasks.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          py: 1,
                          mt: 1,
                          // mb: "88px",
                          color: "text.secondary",
                          backgroundColor: "rgba(0, 0, 0, 0.03)",
                          borderRadius: 1,
                          border: "1px dashed rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "11px", mb: 1 }}
                        >
                          {t("No more items to load")}
                        </Typography>
                      </Box>
                    )}

                  {!isInitialLoading &&
                    !isLoadingMore &&
                    tasks.length === 0 && (
                      <>
                        {noSprintTask ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              py: 4,
                              px: 2,
                              color: "text.secondary",
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                              borderRadius: 1,
                              border: "1px dashed rgba(0, 0, 0, 0.08)",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "14px",
                                textAlign: "center",
                                fontWeight: "bold",
                              }}
                            >
                              {t("Get started in backlog")}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "14px", textAlign: "center" }}
                            >
                              {t("Plan and start a sprint to see work here.")}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="large"
                              onClick={() => {
                                dispatch(setSelectedItem("Backlog"));
                                navigate(
                                  `/defect-tracker/projects/${id}/backlog`,
                                );
                              }}
                              sx={{
                                fontSize: "14px",
                                textTransform: "none",
                                borderRadius: 1,
                                fontWeight: "bold",
                              }}
                            >
                              {t("Go to Backlog")}
                            </Button>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              py: 4,
                              color: "text.secondary",
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                              borderRadius: 1,
                              border: "1px dashed rgba(0, 0, 0, 0.08)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "12px", mb: 1 }}
                            >
                              {t("No tasks in this column")}
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={handleCreateTask}
                              sx={{
                                fontSize: "11px",
                                textTransform: "none",
                                color: "primary.main",
                                backgroundColor: "transparent",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                              }}
                            >
                              {t("create task")}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                </Box>
              )}
            </Box>
          )}
        </Droppable>
      )}
    </Box>
  );
};

export default Column;
